<template>
  <div class="fixed h-0">
    <p v-if="showDebugText" class="left-0 top-0 text-white">
      {{ input }}
    </p>
    <input
      inputmode="none"
      id="hidden-action-input"
      ref="inputRef"
      v-model="input"
      :disabled="isModalOpened"
      @keyup.enter="onEmit"
      class="h-0 w-0 opacity-0"
      autocomplete="off"
    />
  </div>

  <AcceptanceModal
    :is-open="isModalOpened"
    title="Bem vindo de volta!"
    message="Vamos voltar com a operação?"
    confirm-text="Voltar"
    @close="closeModal"
    @confirm="confirmReturn"
    :closable="false"
  />
</template>

<script setup lang="ts">
import { showDefaultToast } from '@/helpers/toast-models'
import { isEventBeenListened, useEventBus } from '@/plugins/eventBus/eventBus'
import { onMounted, onUnmounted, ref, computed } from 'vue'
import AcceptanceModal from '../AcceptanceModal/AcceptanceModal.vue'
import { Mutex } from 'async-mutex'
import * as Sentry from '@sentry/vue'
import { useAppStore } from '@/stores/app.store'

const { logger } = Sentry

const eventBus = useEventBus()
const appStore = useAppStore()

interface Props {
  showDebugText?: boolean
  modelValue?: string
}

const props = withDefaults(defineProps<Props>(), {
  showDebugText: false,
  modelValue: ''
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref<HTMLInputElement | null>(null)
const input = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  }
})
const isModalOpened = ref(false)

const mutex = new Mutex()
let lastSubmittedAt = 0
const SUBMIT_INTERVAL = 500

async function onEmit() {
  const now = Date.now()
  await mutex.runExclusive(async () => {
    if (now - lastSubmittedAt < SUBMIT_INTERVAL) {
      console.log('Ignorado: chamado em menos de 500ms de intervalo')
      return
    }
    lastSubmittedAt = now

    let newInput = input.value.trim()

    const invertedInput = newInput.split('').reverse().join('')

    if (invertedInput.startsWith('http') || invertedInput.startsWith('@@@|')) {
      newInput = invertedInput
    }

    const isCommand = newInput.startsWith('@@@|')

    const command = isCommand ? newInput.replace('@@@|', '') : ''

    if (isCommand) {
      const isListening = isEventBeenListened(command)

      if (!isListening) {
        showDefaultToast('Comando inválido', 'error', 2000)
      }

      eventBus.emit(command as any)

      input.value = ''
    }

    if (appStore.appCurrentPage === 'shipping') {
      logger.info(`[HIDDEN INPUT - SHIPPING] - Valor lido: ${newInput}`)
    }

    eventBus.emit('submit', newInput)
  })
}

const closeModal = async () => {
  isModalOpened.value = false
  inputRef.value?.focus()
}

const confirmReturn = async () => {
  const WarrantyApiService = (await import('@/services/WarrantyApiService/WarrantyApiService'))
    .default
  await WarrantyApiService.checkToken()
  closeModal()
}

function focusMainInput() {
  eventBus.on('clear', () => {
    input.value = ''
  })

  document.addEventListener('visibilitychange', async function () {
    const { useUserStore } = await import('@/stores/user.store')
    const userStore = useUserStore()
    if (!document.hidden && userStore.jwtToken) {
      isModalOpened.value = true
      eventBus.emit('clear')
    }
  })

  setInterval(() => {
    const activeElement = document.activeElement as HTMLElement

    if (
      activeElement.tagName !== 'INPUT' &&
      activeElement.tagName !== 'SELECT' &&
      activeElement.id !== 'hidden-action-input'
    ) {
      inputRef.value?.focus()
    }
  }, 100)
}

onMounted(focusMainInput)
onUnmounted(() => {
  eventBus.off('clear')
})
</script>
